import { useState, useEffect, useRef } from 'react'
import { API_URL } from '../constant/api'

/* ─── Fonts ──────────────────────────────────────────────────── */
if (typeof document !== 'undefined') {
  const id = 'kudos-fonts'
  if (!document.getElementById(id)) {
    const l = document.createElement('link')
    l.id = id; l.rel = 'stylesheet'
    l.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap'
    document.head.appendChild(l)
  }
}

/* ─── Tokens ─────────────────────────────────────────────────── */
const C = {
  dark    : '#060D1F',
  darkCard: '#0A1228',
  darkLine: '#111D36',
  orange  : '#E8440A',
  orangeDim: 'rgba(232,68,10,0.18)',
  orangeGlow: 'rgba(232,68,10,0.08)',
  white   : '#FFFFFF',
  snow    : '#F9F8F6',
  border  : '#EBEBEB',
  borderDark: '#111D36',
  text    : '#0C0B09',
  textMed : '#52493C',
  textSoft: '#9A9182',
  textFaint:'#C8C0B0',
}
const F = {
  sans: "'Inter', 'Plus Jakarta Sans', sans-serif",
  body: "'Plus Jakarta Sans', sans-serif",
}

/* ─── Hooks ──────────────────────────────────────────────────── */
function useInView(opts = {}) {
  const ref = useRef(null)
  const [vis, setVis] = useState(false)
  useEffect(() => {
    const el = ref.current; if (!el) return
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVis(true); obs.disconnect() } }, { threshold: 0.08, ...opts })
    obs.observe(el)
    return () => obs.disconnect()
  }, [])
  return [ref, vis]
}

function useCounter(target, run) {
  const [v, setV] = useState(0)
  useEffect(() => {
    if (!run) return
    let raf
    const dur = 2200, t0 = performance.now()
    const tick = now => {
      const p = Math.min((now - t0) / dur, 1)
      setV(Math.round((1 - Math.pow(1 - p, 3)) * target))
      if (p < 1) raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [run, target])
  return v
}

function useIsMobile(bp = 768) {
  const [m, setM] = useState(() => typeof window !== 'undefined' ? window.innerWidth < bp : false)
  useEffect(() => {
    const h = () => setM(window.innerWidth < bp)
    window.addEventListener('resize', h)
    return () => window.removeEventListener('resize', h)
  }, [bp])
  return m
}

/* ─── Ion wrapper ────────────────────────────────────────────── */
const Ion = ({ name, size = 18, color = 'currentColor', style: s = {} }) => (
  <ion-icon name={name} style={{ fontSize: size, color, display: 'block', flexShrink: 0, ...s }} />
)

/* ─── Logo ───────────────────────────────────────────────────── */
function KudosLogo({ size = 34, textSize = 17, textColor = C.text, textLabel = 'Kudos Live' }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
      <div style={{
        width: size, height: size, borderRadius: Math.round(size * 0.26),
        background: `linear-gradient(145deg, ${C.orange} 0%, #c9380a 100%)`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: `0 ${Math.round(size * 0.09)}px ${Math.round(size * 0.35)}px rgba(232,68,10,0.30)`,
        flexShrink: 0, position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '50%', background: 'rgba(255,255,255,0.12)', pointerEvents: 'none' }} />
        <Ion name="bag-outline" size={Math.round(size * 0.56)} color="#fff" style={{ '--ionicon-stroke-width': '44px' }} />
      </div>
      <span style={{ fontFamily: F.sans, fontWeight: 800, fontSize: textSize, letterSpacing: '-0.03em', color: textColor, lineHeight: 1 }}>
        {textLabel}
      </span>
    </div>
  )
}

/* ─── Markdown renderer (used by LegalPage) ───────────────────── */
function renderMarkdown(content) {
  return content
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/^### (.+)$/gm, `<h3 style="font-size:15px;font-weight:800;margin:22px 0 8px;color:${C.text};font-family:${F.sans};letter-spacing:-0.01em">$1</h3>`)
    .replace(/^## (.+)$/gm,  `<h2 style="font-size:18px;font-weight:800;margin:30px 0 10px;color:${C.text};font-family:${F.sans};letter-spacing:-0.02em;padding-bottom:8px;border-bottom:1px solid #EBEBEB">$1</h2>`)
    .replace(/^# (.+)$/gm,   `<h1 style="font-size:22px;font-weight:900;margin:0 0 18px;color:${C.text};font-family:${F.sans};letter-spacing:-0.03em">$1</h1>`)
    .replace(/\*\*(.+?)\*\*/g, `<strong style="font-weight:700;color:${C.text}">$1</strong>`)
    .replace(/_(.+?)_/g, '<em>$1</em>')
    .replace(/^---$/gm, `<hr style="border:none;border-top:1px solid #EBEBEB;margin:24px 0">`)
    .replace(/^- (.+)$/gm, `<li style="margin:5px 0;padding-left:4px;color:${C.textMed};font-family:${F.sans};font-size:14px;line-height:1.7">$1</li>`)
    .replace(/^(\d+)\. (.+)$/gm, `<li style="margin:5px 0;list-style-type:decimal;padding-left:4px;color:${C.textMed};font-family:${F.sans};font-size:14px;line-height:1.7">$2</li>`)
    .replace(/(<li[^>]*>.*<\/li>\n?)+/g, m => `<ul style="padding-left:22px;margin:10px 0">${m}</ul>`)
    .replace(/^(?!<[h|l|u|h|d|p]).+$/gm, line => line.trim() ? `<p style="margin:10px 0;line-height:1.85;color:${C.textMed};font-family:${F.sans};font-size:14px">${line}</p>` : '')
}

/* ─── Standalone legal page — own URL for /privacy, /terms ───── */
export function LegalPage({ type }) {
  const [doc, setDoc] = useState(null)
  const [error, setError] = useState('')
  const label = type === 'privacy' ? 'Privacy Policy' : 'Terms of Service'

  useEffect(() => {
    fetch(`${API_URL}/terms/${type}`)
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(data => setDoc(data.document))
      .catch(() => setError('Could not load this document. Please try again later.'))
  }, [type])

  return (
    <div style={{ fontFamily: F.body, background: C.white, color: C.text, minHeight: '100vh' }}>
      <nav style={{ height: 64, padding: '0 24px', display: 'flex', alignItems: 'center', borderBottom: `1px solid ${C.border}` }}>
        <a href="/"><KudosLogo size={28} textSize={15} textColor={C.text} /></a>
      </nav>
      <div style={{ maxWidth: 760, margin: '0 auto', padding: '48px 24px 100px' }}>
        <h1 style={{ fontFamily: F.sans, fontSize: 30, fontWeight: 900, letterSpacing: '-0.03em', marginBottom: 24 }}>{label}</h1>
        {error && <div style={{ color: C.textSoft, fontSize: 14, fontFamily: F.sans }}>{error}</div>}
        {doc ? <div dangerouslySetInnerHTML={{ __html: renderMarkdown(doc.content) }} /> : !error && <div style={{ color: C.textSoft, fontSize: 14, fontFamily: F.sans }}>Loading…</div>}
      </div>
    </div>
  )
}

/* ─── Phone shell ────────────────────────────────────────────── */
const PhoneShell = ({ children, style: s = {} }) => (
  <div style={{
    width: 252, height: 508, borderRadius: 40, background: '#030918',
    border: '6px solid #0D1530', boxShadow: '0 48px 96px rgba(6,13,31,0.55), inset 0 0 0 1px rgba(255,255,255,0.04)',
    overflow: 'hidden', position: 'relative', flexShrink: 0, ...s,
  }}>
    <div style={{ position: 'absolute', top: 9, left: '50%', transform: 'translateX(-50%)', width: 72, height: 10, background: '#030918', borderRadius: 99, zIndex: 10 }} />
    <div style={{ width: '100%', height: '100%', overflow: 'hidden', background: C.dark }}>{children}</div>
  </div>
)

/* ─── Live phone mockup ──────────────────────────────────────── */
function LivePhoneContent() {
  const [liked, setLiked] = useState(false)
  const [likes, setLikes] = useState(1240)
  const msgs = [
    { u: 'Alice', m: 'Love this fabric!', c: C.orange },
    { u: 'Jean',  m: 'Is this in stock?', c: '#7C3AED' },
    { u: 'Diane', m: 'Price for size M?',  c: '#0891B2' },
  ]
  return (
    <div style={{ width: '100%', height: '100%', background: 'linear-gradient(170deg,#060D1F 0%,#0D1835 50%,#060D1F 100%)', display: 'flex', flexDirection: 'column', position: 'relative' }}>
      <div style={{ padding: '22px 12px 8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, background: C.orange, borderRadius: 7, padding: '3px 8px' }}>
          <div style={{ width: 5, height: 5, borderRadius: '50%', background: '#fff', animation: 'kpulse 1.2s infinite' }} />
          <span style={{ fontSize: 9, fontWeight: 800, color: '#fff', letterSpacing: '0.08em', fontFamily: F.sans }}>LIVE</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'rgba(255,255,255,0.1)', borderRadius: 7, padding: '3px 8px', backdropFilter: 'blur(6px)' }}>
          <Ion name="eye-outline" size={9} color="rgba(255,255,255,0.7)" />
          <span style={{ fontSize: 9, fontWeight: 700, color: 'rgba(255,255,255,0.9)', fontFamily: F.sans }}>2,418</span>
        </div>
      </div>
      <div style={{ padding: '0 12px', display: 'flex', alignItems: 'center', gap: 7 }}>
        <div style={{ width: 28, height: 28, borderRadius: '50%', background: `linear-gradient(135deg,${C.orange},#f76b1c)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, color: '#fff', fontFamily: F.sans }}>F</div>
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#fff', fontFamily: F.sans }}>Fashion House RW</div>
          <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.45)', fontFamily: F.sans }}>Kigali, Rwanda</div>
        </div>
        <div style={{ marginLeft: 'auto', background: 'rgba(232,68,10,0.2)', border: '1px solid rgba(232,68,10,0.4)', borderRadius: 7, padding: '3px 8px', fontSize: 9, fontWeight: 700, color: C.orange, cursor: 'pointer', fontFamily: F.sans }}>Follow</div>
      </div>
      <div style={{ margin: '10px 12px', background: 'rgba(255,255,255,0.07)', borderRadius: 14, padding: 10, border: '1px solid rgba(255,255,255,0.08)', backdropFilter: 'blur(10px)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 44, height: 44, borderRadius: 10, background: 'rgba(232,68,10,0.15)', border: '1px solid rgba(232,68,10,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Ion name="shirt-outline" size={22} color={C.orange} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 9.5, fontWeight: 600, color: 'rgba(255,255,255,0.8)', fontFamily: F.sans }}>Kigali Summer Dress</div>
            <div style={{ fontSize: 13, fontWeight: 800, color: C.orange, fontFamily: F.sans, marginTop: 1 }}>RWF 28,000</div>
          </div>
          <button style={{ background: C.orange, color: '#fff', border: 'none', borderRadius: 8, padding: '5px 10px', fontSize: 9, fontWeight: 700, cursor: 'pointer', fontFamily: F.sans }}>Buy</button>
        </div>
      </div>
      <div style={{ flex: 1, padding: '0 12px', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', gap: 5 }}>
        {msgs.map(({ u, m, c }) => (
          <div key={u} style={{ background: 'rgba(255,255,255,0.07)', backdropFilter: 'blur(6px)', borderRadius: 9, padding: '5px 9px', fontSize: 9, border: '1px solid rgba(255,255,255,0.06)' }}>
            <span style={{ fontWeight: 700, color: c, fontFamily: F.sans }}>{u} </span>
            <span style={{ color: 'rgba(255,255,255,0.6)', fontFamily: F.sans }}>{m}</span>
          </div>
        ))}
      </div>
      <div style={{ padding: '8px 12px 14px', display: 'flex', gap: 7, alignItems: 'center' }}>
        <div style={{ flex: 1, background: 'rgba(255,255,255,0.07)', borderRadius: 18, padding: '6px 12px', fontSize: 9, color: 'rgba(255,255,255,0.3)', fontFamily: F.sans, border: '1px solid rgba(255,255,255,0.06)' }}>Say something…</div>
        <div onClick={() => { setLiked(p => !p); setLikes(p => p + (liked ? -1 : 1)) }}
          style={{ width: 30, height: 30, borderRadius: '50%', background: liked ? C.orange : 'rgba(255,255,255,0.07)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'all .2s', border: '1px solid rgba(255,255,255,0.08)' }}>
          <Ion name={liked ? 'heart' : 'heart-outline'} size={14} color={liked ? '#fff' : 'rgba(255,255,255,0.6)'} />
        </div>
      </div>
    </div>
  )
}

/* ─── Stat counter ───────────────────────────────────────────── */
function StatCount({ n, suffix = '', label, run }) {
  const v = useCounter(n, run)
  const display = run ? (n >= 1000 ? (v >= 1000 ? (v / 1000).toFixed(v >= 10000 ? 0 : 1) + 'K' : v) : v) + suffix : '—'
  return (
    <div>
      <div style={{ fontFamily: F.sans, fontSize: 'clamp(26px,7vw,38px)', fontWeight: 900, letterSpacing: '-0.05em', color: C.white, lineHeight: 1 }}>{display}</div>
      <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginTop: 6, fontWeight: 500, letterSpacing: '0.04em', fontFamily: F.sans }}>{label}</div>
    </div>
  )
}

/* ─── Feature card ───────────────────────────────────────────── */
function FeatureCard({ icon, title, desc, delay = 0, vis }) {
  return (
    <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 16, padding: '24px 20px', opacity: vis ? 1 : 0, transform: vis ? 'none' : 'translateY(18px)', transition: `opacity .5s ${delay}s, transform .5s ${delay}s` }}>
      <div style={{ width: 44, height: 44, borderRadius: 12, background: 'rgba(232,68,10,0.08)', border: '1px solid rgba(232,68,10,0.14)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
        <Ion name={icon} size={22} color={C.orange} />
      </div>
      <div style={{ fontFamily: F.sans, fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 7, letterSpacing: '-0.01em' }}>{title}</div>
      <div style={{ fontSize: 13, color: C.textMed, lineHeight: 1.7 }}>{desc}</div>
    </div>
  )
}

/* ─── Check list item ────────────────────────────────────────── */
const CheckItem = ({ children, iconColor = C.orange }) => (
  <li style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 13.5, color: C.textMed, lineHeight: 1.55 }}>
    <div style={{ width: 20, height: 20, borderRadius: '50%', background: `rgba(232,68,10,0.1)`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
      <Ion name="checkmark-outline" size={12} color={iconColor} />
    </div>
    {children}
  </li>
)

/* ─── Help Center Modal ──────────────────────────────────────── */
function HelpCenterModal({ onClose, onContact, mob }) {
  const [faqs,    setFaqs]    = useState([])
  const [loading, setLoading] = useState(true)
  const [search,  setSearch]  = useState('')
  const [open,    setOpen]    = useState(null)

  useEffect(() => {
    fetch(`${API_URL}/helpcenter/faqs`)
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(d => setFaqs(d.faqs || []))
      .catch(() => setFaqs([
        { faq_id:1, category:'Order',    question:'How do I track my order?',          answer:'Go to Orders tab → tap the order → you will see real-time delivery status on a live map.' },
        { faq_id:2, category:'Payment',  question:'What payment methods are accepted?', answer:'We accept MTN MoMo, Airtel Money, and iPay/Irembo. No credit card or bank account required.' },
        { faq_id:3, category:'Account',  question:'How do I become a seller?',          answer:'Go to Settings → Register as Seller → fill in your details. Approval takes 24–48 hours.' },
        { faq_id:4, category:'Delivery', question:'What is the delivery fee?',          answer:'Delivery fees vary by zone. The exact fee is shown at checkout before you confirm your order.' },
        { faq_id:5, category:'Account',  question:'How do I reset my password?',        answer:'On the login screen tap "Forgot password" and enter your phone number. You\'ll receive an OTP to reset.' },
        { faq_id:6, category:'Order',    question:'Can I cancel my order?',             answer:'Orders can be cancelled within 5 minutes of placement. After that, contact support via the Help button.' },
        { faq_id:7, category:'Payment',  question:'When will my refund arrive?',        answer:'Approved refunds are processed within 3–7 business days back to your original payment method.' },
        { faq_id:8, category:'Delivery', question:'Do you deliver outside Kigali?',     answer:'Currently we deliver within Kigali. Delivery to other provinces is coming soon.' },
      ]))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    const h = e => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [onClose])

  const filtered = faqs.filter(f =>
    !search || f.question.toLowerCase().includes(search.toLowerCase()) || f.answer.toLowerCase().includes(search.toLowerCase())
  )
  const byCategory = filtered.reduce((acc, f) => {
    const c = f.category || 'General'; if (!acc[c]) acc[c] = []; acc[c].push(f); return acc
  }, {})

  const catIcon = { Order:'bag-outline', Payment:'card-outline', Account:'person-outline', Delivery:'navigate-outline', Technical:'settings-outline', General:'help-circle-outline' }

  return (
    <>
      <div onClick={onClose} style={{ position:'fixed', inset:0, background:'rgba(8,7,6,0.6)', zIndex:900, backdropFilter:'blur(6px)' }} />
      <div style={{
        position:'fixed', top: mob?'2vh':'4vh', left:'50%', transform:'translateX(-50%)',
        width: mob?'96%':'100%', maxWidth: mob?'none':780,
        maxHeight: mob?'96vh':'92vh',
        background:C.white, borderRadius: mob?16:22, zIndex:901,
        display:'flex', flexDirection:'column',
        boxShadow:'0 40px 120px rgba(0,0,0,0.22)', overflow:'hidden',
        animation:'legalPopIn 0.25s cubic-bezier(0.22,1,0.36,1)',
      }}>
        {/* Header */}
        <div style={{ padding: mob?'18px 20px 16px':'24px 28px 20px', borderBottom:`1px solid ${C.border}`, display:'flex', alignItems:'flex-start', justifyContent:'space-between', flexShrink:0 }}>
          <div>
            <div style={{ fontSize:10, fontWeight:700, color:C.orange, letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:4, fontFamily:F.sans }}>Kudos Live</div>
            <div style={{ fontFamily:F.sans, fontSize: mob?17:20, fontWeight:800, color:C.text, letterSpacing:'-0.02em' }}>Help Center</div>
            <div style={{ fontSize:13, color:C.textSoft, marginTop:4, fontFamily:F.sans }}>Find answers to the most common questions.</div>
          </div>
          <button onClick={onClose} style={{ width:34, height:34, borderRadius:8, border:`1px solid ${C.border}`, background:C.snow, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, marginLeft:16 }}>
            <Ion name="close-outline" size={18} color={C.textSoft} />
          </button>
        </div>

        {/* Search */}
        <div style={{ padding: mob?'12px 20px':'14px 28px', borderBottom:`1px solid ${C.border}`, flexShrink:0 }}>
          <div style={{ display:'flex', alignItems:'center', gap:10, background:C.snow, border:`1px solid ${C.border}`, borderRadius:10, padding:'10px 14px' }}>
            <Ion name="search-outline" size={17} color={C.textSoft} />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search questions…"
              style={{ flex:1, border:'none', outline:'none', background:'transparent', fontSize:14, color:C.text, fontFamily:F.sans }}
            />
            {search && <div onClick={() => setSearch('')} style={{ cursor:'pointer', color:C.textSoft, fontSize:18, lineHeight:1 }}>×</div>}
          </div>
        </div>

        {/* FAQ list */}
        <div style={{ flex:1, overflowY:'auto', padding: mob?'16px 20px 20px':'20px 28px 28px' }}>
          {loading && <div style={{ textAlign:'center', padding:'60px 0', color:C.textSoft, fontSize:14, fontFamily:F.sans }}>Loading…</div>}
          {!loading && Object.keys(byCategory).length === 0 && (
            <div style={{ textAlign:'center', padding:'60px 0', color:C.textSoft, fontSize:14, fontFamily:F.sans }}>No results for "{search}"</div>
          )}
          {!loading && Object.entries(byCategory).map(([cat, items]) => (
            <div key={cat} style={{ marginBottom:24 }}>
              <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10 }}>
                <div style={{ width:26, height:26, borderRadius:7, background:'rgba(232,68,10,0.08)', border:'1px solid rgba(232,68,10,0.12)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                  <Ion name={catIcon[cat]||'help-circle-outline'} size={14} color={C.orange} />
                </div>
                <span style={{ fontSize:11, fontWeight:700, color:C.textSoft, textTransform:'uppercase', letterSpacing:'0.1em', fontFamily:F.sans }}>{cat}</span>
              </div>
              <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                {items.map(faq => (
                  <div key={faq.faq_id}
                    style={{ border:`1px solid ${open===faq.faq_id?'rgba(232,68,10,0.2)':C.border}`, borderRadius:12, overflow:'hidden', background: open===faq.faq_id?'rgba(232,68,10,0.02)':C.white, transition:'all 0.2s' }}>
                    <button
                      onClick={() => setOpen(o => o===faq.faq_id ? null : faq.faq_id)}
                      style={{ width:'100%', padding:'14px 16px', display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, background:'transparent', border:'none', cursor:'pointer', textAlign:'left' }}>
                      <span style={{ fontSize:14, fontWeight:600, color:C.text, fontFamily:F.sans, lineHeight:1.4, flex:1 }}>{faq.question}</span>
                      <div style={{ width:22, height:22, borderRadius:'50%', background: open===faq.faq_id?C.orange:'rgba(232,68,10,0.08)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, transition:'background 0.2s' }}>
                        <Ion name={open===faq.faq_id?'remove':'add'} size={14} color={open===faq.faq_id?'#fff':C.orange} />
                      </div>
                    </button>
                    {open === faq.faq_id && (
                      <div style={{ padding:'0 16px 16px', fontSize:14, color:C.textMed, lineHeight:1.8, fontFamily:F.sans, borderTop:`1px solid rgba(232,68,10,0.1)`, paddingTop:12 }}>
                        {faq.answer}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Footer CTA */}
        <div style={{ padding: mob?'14px 20px':'16px 28px', borderTop:`1px solid ${C.border}`, background:C.snow, display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0, gap:12, flexWrap:'wrap' }}>
          <div style={{ fontSize:13, color:C.textMed, fontFamily:F.sans }}>
            Still need help? <span style={{ color:C.text, fontWeight:600 }}>Our team is here.</span>
          </div>
          <button onClick={onContact} style={{ height:38, padding:'0 18px', borderRadius:9, border:'none', background:C.orange, color:'#fff', fontSize:13, fontWeight:700, cursor:'pointer', fontFamily:F.sans, display:'flex', alignItems:'center', gap:7, boxShadow:'0 4px 14px rgba(232,68,10,0.25)', flexShrink:0 }}>
            <Ion name="chatbubble-outline" size={15} color="#fff" />
            Contact Support
          </button>
        </div>
      </div>
    </>
  )
}

/* ─── Contact Modal ──────────────────────────────────────────── */
function ContactModal({ onClose, mob }) {
  const [form,     setForm]     = useState({ name:'', contact:'', subject:'', category:'General', message:'' })
  const [sending,  setSending]  = useState(false)
  const [sent,     setSent]     = useState(false)
  const [error,    setError]    = useState('')
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  useEffect(() => {
    const h = e => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [onClose])

  const handleSubmit = async (e) => {
    e.preventDefault(); setSending(true); setError('')
    try {
      const res = await fetch(`${API_URL}/helpcenter/tickets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ subject: form.subject, category: form.category, message: form.message, user_name: form.name, user_contact: form.contact }),
      })
      if (!res.ok) throw new Error()
      setSent(true)
    } catch {
      setError('Could not send your message. Please email us directly at support@kudos.rw')
    } finally { setSending(false) }
  }

  const CONTACTS = [
    { icon:'mail-outline',      label:'Email',     value:'support@kudos.rw',  href:'mailto:support@kudos.rw' },
    { icon:'logo-whatsapp',     label:'WhatsApp',  value:'+250 788 000 000',   href:'https://wa.me/250788000000' },
    { icon:'location-outline',  label:'Location',  value:'Kigali, Rwanda',     href:null },
    { icon:'time-outline',      label:'Hours',     value:'Mon–Sat, 8am–8pm',   href:null },
  ]

  return (
    <>
      <div onClick={onClose} style={{ position:'fixed', inset:0, background:'rgba(8,7,6,0.6)', zIndex:900, backdropFilter:'blur(6px)' }} />
      <div style={{
        position:'fixed', top: mob?'2vh':'4vh', left:'50%', transform:'translateX(-50%)',
        width: mob?'96%':'100%', maxWidth: mob?'none':820,
        maxHeight: mob?'96vh':'92vh',
        background:C.white, borderRadius: mob?16:22, zIndex:901,
        display:'flex', flexDirection:'column',
        boxShadow:'0 40px 120px rgba(0,0,0,0.22)', overflow:'hidden',
        animation:'legalPopIn 0.25s cubic-bezier(0.22,1,0.36,1)',
      }}>
        {/* Header */}
        <div style={{ padding: mob?'18px 20px 16px':'24px 28px 20px', borderBottom:`1px solid ${C.border}`, display:'flex', alignItems:'flex-start', justifyContent:'space-between', flexShrink:0 }}>
          <div>
            <div style={{ fontSize:10, fontWeight:700, color:C.orange, letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:4, fontFamily:F.sans }}>Kudos Live</div>
            <div style={{ fontFamily:F.sans, fontSize: mob?17:20, fontWeight:800, color:C.text, letterSpacing:'-0.02em' }}>Contact Us</div>
            <div style={{ fontSize:13, color:C.textSoft, marginTop:4, fontFamily:F.sans }}>We typically respond within a few hours.</div>
          </div>
          <button onClick={onClose} style={{ width:34, height:34, borderRadius:8, border:`1px solid ${C.border}`, background:C.snow, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, marginLeft:16 }}>
            <Ion name="close-outline" size={18} color={C.textSoft} />
          </button>
        </div>

        <div style={{ flex:1, overflowY:'auto', display:'flex', flexDirection: mob?'column':'row', minHeight:0 }}>
          {/* Left: contact info */}
          <div style={{ flexShrink:0, width: mob?'100%':300, padding: mob?'20px 20px 0':'28px 28px', background: mob?C.white:C.dark, display:'flex', flexDirection:'column', gap:0 }}>
            {!mob && (
              <div style={{ marginBottom:28 }}>
                <div style={{ fontFamily:F.sans, fontSize:17, fontWeight:800, color:C.white, marginBottom:6 }}>Get in touch</div>
                <div style={{ fontSize:13, color:'rgba(255,255,255,0.45)', fontFamily:F.sans, lineHeight:1.65 }}>Have a question, problem with an order, or just want to say hello — we're here for it.</div>
              </div>
            )}
            <div style={{ display:'flex', flexDirection: mob?'row':'column', gap: mob?10:10, flexWrap:'wrap' }}>
              {CONTACTS.map(({ icon, label, value, href }) => (
                <div key={label} style={{ display:'flex', alignItems:'center', gap:12, padding: mob?'10px 14px':'12px 14px', background: mob?C.snow:' rgba(255,255,255,0.05)', border:`1px solid ${mob?C.border:'rgba(255,255,255,0.08)'}`, borderRadius:10, flex: mob?'1 1 42%':undefined }}>
                  <div style={{ width:32, height:32, borderRadius:8, background:'rgba(232,68,10,0.12)', border:'1px solid rgba(232,68,10,0.18)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                    <Ion name={icon} size={16} color={C.orange} />
                  </div>
                  <div style={{ minWidth:0 }}>
                    <div style={{ fontSize:10, fontWeight:700, color: mob?C.textSoft:'rgba(255,255,255,0.35)', textTransform:'uppercase', letterSpacing:'0.08em', fontFamily:F.sans }}>{label}</div>
                    {href
                      ? <a href={href} target="_blank" rel="noreferrer" style={{ fontSize:12, fontWeight:600, color: mob?C.text:C.white, fontFamily:F.sans, display:'block', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{value}</a>
                      : <div style={{ fontSize:12, fontWeight:600, color: mob?C.text:C.white, fontFamily:F.sans }}>{value}</div>
                    }
                  </div>
                </div>
              ))}
            </div>
            {!mob && (
              <div style={{ marginTop:'auto', paddingTop:28 }}>
                <div style={{ fontSize:11, color:'rgba(255,255,255,0.2)', fontFamily:F.sans }}>Rwanda · Kigali · © 2025 Kudos Live</div>
              </div>
            )}
          </div>

          {/* Right: form */}
          <div style={{ flex:1, padding: mob?'20px 20px 28px':'28px 32px', overflowY:'auto' }}>
            {sent ? (
              <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', height:'100%', minHeight:240, textAlign:'center', gap:14 }}>
                <div style={{ width:56, height:56, borderRadius:'50%', background:'rgba(21,128,61,0.1)', border:'1px solid rgba(21,128,61,0.2)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                  <Ion name="checkmark-outline" size={28} color="#15803d" />
                </div>
                <div style={{ fontFamily:F.sans, fontSize:18, fontWeight:800, color:C.text }}>Message sent!</div>
                <div style={{ fontSize:14, color:C.textMed, fontFamily:F.sans, maxWidth:280, lineHeight:1.65 }}>We've received your message and will get back to you as soon as possible.</div>
                <button onClick={onClose} style={{ marginTop:8, height:40, padding:'0 24px', borderRadius:9, border:'none', background:C.orange, color:'#fff', fontSize:13, fontWeight:700, cursor:'pointer', fontFamily:F.sans }}>Close</button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} style={{ display:'flex', flexDirection:'column', gap:16 }}>
                <div style={{ fontFamily:F.sans, fontSize:15, fontWeight:800, color:C.text, marginBottom:4 }}>Send us a message</div>

                <div style={{ display:'grid', gridTemplateColumns: mob?'1fr':'1fr 1fr', gap:12 }}>
                  <div>
                    <label style={{ display:'block', fontSize:11, fontWeight:700, color:C.textSoft, textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:6, fontFamily:F.sans }}>Your Name</label>
                    <input required value={form.name} onChange={e => set('name', e.target.value)} placeholder="Full name"
                      style={{ width:'100%', height:42, borderRadius:9, border:`1px solid ${C.border}`, padding:'0 13px', fontSize:13, color:C.text, outline:'none', fontFamily:F.sans, boxSizing:'border-box', background:C.white }} />
                  </div>
                  <div>
                    <label style={{ display:'block', fontSize:11, fontWeight:700, color:C.textSoft, textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:6, fontFamily:F.sans }}>Phone or Email</label>
                    <input required value={form.contact} onChange={e => set('contact', e.target.value)} placeholder="078…  or  you@example.com"
                      style={{ width:'100%', height:42, borderRadius:9, border:`1px solid ${C.border}`, padding:'0 13px', fontSize:13, color:C.text, outline:'none', fontFamily:F.sans, boxSizing:'border-box', background:C.white }} />
                  </div>
                </div>

                <div style={{ display:'grid', gridTemplateColumns: mob?'1fr':'1fr 1fr', gap:12 }}>
                  <div>
                    <label style={{ display:'block', fontSize:11, fontWeight:700, color:C.textSoft, textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:6, fontFamily:F.sans }}>Category</label>
                    <select value={form.category} onChange={e => set('category', e.target.value)}
                      style={{ width:'100%', height:42, borderRadius:9, border:`1px solid ${C.border}`, padding:'0 13px', fontSize:13, color:C.text, outline:'none', fontFamily:F.sans, background:C.white, cursor:'pointer', boxSizing:'border-box' }}>
                      {['General','Order','Payment','Delivery','Account','Technical','Other'].map(c => <option key={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ display:'block', fontSize:11, fontWeight:700, color:C.textSoft, textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:6, fontFamily:F.sans }}>Subject</label>
                    <input required value={form.subject} onChange={e => set('subject', e.target.value)} placeholder="Brief description"
                      style={{ width:'100%', height:42, borderRadius:9, border:`1px solid ${C.border}`, padding:'0 13px', fontSize:13, color:C.text, outline:'none', fontFamily:F.sans, boxSizing:'border-box', background:C.white }} />
                  </div>
                </div>

                <div>
                  <label style={{ display:'block', fontSize:11, fontWeight:700, color:C.textSoft, textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:6, fontFamily:F.sans }}>Message</label>
                  <textarea required value={form.message} onChange={e => set('message', e.target.value)} placeholder="Describe your issue or question in detail…" rows={5}
                    style={{ width:'100%', borderRadius:9, border:`1px solid ${C.border}`, padding:'11px 13px', fontSize:13, color:C.text, outline:'none', fontFamily:F.sans, resize:'vertical', lineHeight:1.6, background:C.white, boxSizing:'border-box' }} />
                </div>

                {error && (
                  <div style={{ background:'#fef2f2', border:'1px solid #fecaca', borderRadius:9, padding:'10px 14px', fontSize:13, color:'#b91c1c', fontFamily:F.sans }}>
                    {error}
                  </div>
                )}

                <button type="submit" disabled={sending} style={{ height:46, borderRadius:11, border:'none', background:sending?'#f0c4b4':C.orange, color:'#fff', fontSize:14, fontWeight:700, cursor:sending?'not-allowed':'pointer', fontFamily:F.sans, display:'flex', alignItems:'center', justifyContent:'center', gap:8, boxShadow:sending?'none':'0 6px 20px rgba(232,68,10,0.28)', transition:'all 0.2s' }}>
                  {sending ? 'Sending…' : <><Ion name="send-outline" size={16} color="#fff" />Send Message</>}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

/* ═══════════════════════════════════════════════════════════════
   HOMEPAGE
═══════════════════════════════════════════════════════════════ */
export default function HomePage() {
  const [scrollY,    setScrollY]    = useState(0)
  const [showHelp,   setShowHelp]   = useState(false)
  const [showContact,setShowContact] = useState(false)
  const [menuOpen,   setMenuOpen]   = useState(false)
  const [statRef,  statVis]     = useInView()
  const [howRef,   howVis]      = useInView()
  const [featRef,  featVis]     = useInView()
  const [forRef,   forVis]      = useInView()
  const [ctaRef,   ctaVis]      = useInView()
  const mob = useIsMobile()

  useEffect(() => {
    const h = () => setScrollY(window.scrollY)
    window.addEventListener('scroll', h, { passive: true })
    return () => window.removeEventListener('scroll', h)
  }, [])

  // Close menu on scroll
  useEffect(() => { if (scrollY > 10) setMenuOpen(false) }, [scrollY])

  const navSolid = scrollY > 40

  const FEATURES = [
    { icon: 'radio-outline',           title: 'Shoppable Live Streams',  desc: 'Go live and sell in real time. Products appear as tap-to-buy cards directly inside the stream — no redirects.' },
    { icon: 'play-circle-outline',     title: 'Reels with Product Tags', desc: 'Short-form video with products pinned inside. Browse at your pace, tap any item to view and purchase.' },
    { icon: 'card-outline',            title: 'Mobile Money Checkout',   desc: 'MTN MoMo and Airtel Money built in. No credit card or bank account needed — pay the way Rwanda pays.' },
    { icon: 'navigate-circle-outline', title: 'Live Delivery Tracking',  desc: 'A Kudos Live driver picks up and delivers. Watch real-time movement from shop to your door on a live map.' },
    { icon: 'bar-chart-outline',       title: 'Seller Analytics',        desc: 'Revenue, viewers, stream replays, top products — every metric a seller needs to grow, live on their dashboard.' },
    { icon: 'shield-checkmark-outline',title: 'Buyer Protection',        desc: 'Every order is covered. Wrong item, late delivery, damaged goods — resolved within hours, refunded if needed.' },
  ]

  const STEPS = [
    { n: 1, icon: 'phone-portrait-outline', title: 'Download & sign up',      desc: 'Create your account in 30 seconds. Phone number only — no email required.' },
    { n: 2, icon: 'radio-outline',          title: 'Watch or go live',        desc: 'Browse live sellers across Rwanda, or tap Go Live and start selling to thousands instantly.' },
    { n: 3, icon: 'card-outline',           title: 'Buy with mobile money',   desc: 'MTN MoMo or Airtel Money. One tap to checkout — no card or bank account needed.' },
    { n: 4, icon: 'navigate-outline',       title: 'Track your delivery live',desc: 'A Kudos Live driver picks up and delivers. Watch them on a live map to your door.' },
  ]

  const NAV_LINKS = [['#how','How it works'],['#buyers','Buyers'],['#sellers','Sellers'],['#features','Features']]

  return (
    <div style={{ fontFamily: F.body, background: C.white, color: C.text, overflowX: 'hidden' }}>
      <style>{`
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0 }
        html { scroll-behavior: smooth }
        @keyframes kpulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.3;transform:scale(1.6)} }
        @keyframes kfloat { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-12px)} }
        @keyframes kfade  { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:translateY(0)} }
        @keyframes legalPopIn { from{opacity:0;transform:translateX(-50%) translateY(16px) scale(0.97)} to{opacity:1;transform:translateX(-50%) translateY(0) scale(1)} }
        .kfade { animation: kfade .7s both ease }
        .kd1{animation-delay:.05s} .kd2{animation-delay:.15s} .kd3{animation-delay:.25s}
        .kd4{animation-delay:.38s} .kd5{animation-delay:.52s}
        ::-webkit-scrollbar { width: 4px }
        ::-webkit-scrollbar-track { background: ${C.snow} }
        ::-webkit-scrollbar-thumb { background: ${C.border}; border-radius: 99px }
        a { text-decoration: none; color: inherit }
      `}</style>

      {/* ══ NAV ══════════════════════════════════════════════════ */}
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 200,
        height: mob ? 56 : 64,
        padding: mob ? '0 20px' : '0 48px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        background: navSolid || menuOpen ? 'rgba(255,255,255,0.96)' : 'transparent',
        backdropFilter: navSolid || menuOpen ? 'blur(20px)' : 'none',
        borderBottom: navSolid || menuOpen ? `1px solid ${C.border}` : 'none',
        transition: 'all .3s',
      }}>
        <KudosLogo size={28} textSize={15} textColor={navSolid || menuOpen ? C.text : C.white} />

        {/* Desktop nav links */}
        {!mob && (
          <div style={{ display: 'flex', gap: 32 }}>
            {NAV_LINKS.map(([h,l]) => (
              <a key={l} href={h} style={{ fontSize: 13, fontWeight: 500, color: navSolid ? C.textSoft : 'rgba(255,255,255,0.6)', transition: 'color .2s', fontFamily: F.sans }}
                onMouseEnter={e => e.currentTarget.style.color = navSolid ? C.text : '#fff'}
                onMouseLeave={e => e.currentTarget.style.color = navSolid ? C.textSoft : 'rgba(255,255,255,0.6)'}>{l}</a>
            ))}
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button style={{ height: mob ? 34 : 36, padding: '0 14px', borderRadius: 9, border: 'none', background: C.orange, color: '#fff', fontSize: mob ? 12 : 13, fontWeight: 600, cursor: 'pointer', fontFamily: F.sans, display: 'flex', alignItems: 'center', gap: 6 }}>
            <Ion name="download-outline" size={13} color="#fff" />
            {mob ? 'Download' : 'Download App'}
          </button>

          {/* Mobile hamburger */}
          {mob && (
            <div onClick={() => setMenuOpen(p => !p)} style={{ width: 34, height: 34, borderRadius: 8, border: `1px solid ${navSolid || menuOpen ? C.border : 'rgba(255,255,255,0.2)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', background: menuOpen ? C.snow : 'transparent' }}>
              <Ion name={menuOpen ? 'close-outline' : 'menu-outline'} size={20} color={navSolid || menuOpen ? C.text : '#fff'} />
            </div>
          )}
        </div>
      </nav>

      {/* Mobile menu dropdown */}
      {mob && menuOpen && (
        <div style={{ position: 'fixed', top: 56, left: 0, right: 0, zIndex: 199, background: 'rgba(255,255,255,0.98)', backdropFilter: 'blur(20px)', borderBottom: `1px solid ${C.border}`, padding: '12px 0 16px' }}>
          {NAV_LINKS.map(([h,l]) => (
            <a key={l} href={h} onClick={() => setMenuOpen(false)} style={{ display: 'block', padding: '11px 24px', fontSize: 15, fontWeight: 600, color: C.textMed, fontFamily: F.sans }}>
              {l}
            </a>
          ))}
        </div>
      )}

      {/* ══ HERO ══════════════════════════════════════════════════ */}
      <section style={{
        minHeight: '100vh', background: C.dark,
        padding: mob ? '0 20px' : '0 48px',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', inset: 0, backgroundImage: `radial-gradient(ellipse 70% 60% at 65% 50%, rgba(232,68,10,0.09) 0%, transparent 70%), radial-gradient(ellipse 50% 50% at 15% 85%, rgba(29,78,216,0.12) 0%, transparent 60%)`, pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', inset: 0, backgroundImage: `repeating-linear-gradient(0deg, transparent, transparent 71px, rgba(255,255,255,0.025) 71px, rgba(255,255,255,0.025) 72px), repeating-linear-gradient(90deg, transparent, transparent 71px, rgba(255,255,255,0.025) 71px, rgba(255,255,255,0.025) 72px)`, pointerEvents: 'none' }} />

        <div style={{
          maxWidth: 1200, width: '100%',
          display: 'flex', flexDirection: mob ? 'column' : 'row',
          alignItems: mob ? 'flex-start' : 'center',
          gap: mob ? 40 : 72,
          paddingTop: mob ? 80 : 72,
          paddingBottom: mob ? 60 : 0,
        }}>

          {/* Copy */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="kfade kd1" style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: 'rgba(232,68,10,0.12)', border: '1px solid rgba(232,68,10,0.25)', borderRadius: 99, padding: '5px 13px', marginBottom: 24 }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: C.orange, animation: 'kpulse 1.4s infinite' }} />
              <span style={{ fontSize: 11, fontWeight: 600, color: C.orange, letterSpacing: '0.06em', textTransform: 'uppercase', fontFamily: F.sans }}>84 streams live right now</span>
            </div>

            <h1 className="kfade kd2" style={{ fontFamily: F.sans, fontSize: mob ? 'clamp(36px,11vw,52px)' : 'clamp(40px,5.5vw,76px)', fontWeight: 900, letterSpacing: '-0.05em', lineHeight: 1.04, color: C.white, marginBottom: 20 }}>
              The Stream<br />
              <span style={{ color: C.orange }}>is the</span> Store.
            </h1>

            <p className="kfade kd3" style={{ fontSize: mob ? 15 : 17, color: 'rgba(255,255,255,0.52)', lineHeight: 1.72, maxWidth: 460, fontWeight: 400, marginBottom: 32, fontFamily: F.sans }}>
              Rwanda's first shoppable live streaming marketplace. Watch sellers go live, discover products, and buy in real time — or launch your own shop today.
            </p>

            <div className="kfade kd4" style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 20 }}>
              <button style={{ height: 46, padding: '0 22px', borderRadius: 11, border: 'none', background: C.orange, color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: F.sans, display: 'flex', alignItems: 'center', gap: 8, boxShadow: '0 8px 28px rgba(232,68,10,0.3)' }}>
                <Ion name="bag-outline" size={17} color="#fff" />
                Start Shopping
              </button>
              <button style={{ height: 46, padding: '0 22px', borderRadius: 11, border: '1px solid rgba(255,255,255,0.14)', background: 'rgba(255,255,255,0.05)', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: F.sans, display: 'flex', alignItems: 'center', gap: 8 }}>
                <Ion name="storefront-outline" size={17} color="#fff" />
                Open Your Shop
              </button>
            </div>

            <div className="kfade kd5" style={{ display: 'flex', gap: 9, flexWrap: 'wrap' }}>
              {[['logo-apple','App Store'],['logo-android','Google Play']].map(([ic,lbl]) => (
                <div key={lbl} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '8px 14px', borderRadius: 9, border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.04)', fontSize: 12, fontWeight: 500, color: 'rgba(255,255,255,0.55)', cursor: 'pointer', fontFamily: F.sans }}>
                  <Ion name={ic} size={15} color="rgba(255,255,255,0.55)" />{lbl}
                </div>
              ))}
            </div>
          </div>

          {/* Phone + stats — hidden on mobile */}
          {!mob && (
            <div style={{ flexShrink: 0, display: 'flex', gap: 18, alignItems: 'flex-start', paddingTop: 36 }}>
              <PhoneShell style={{ animation: 'kfloat 4.5s ease-in-out infinite' }}>
                <LivePhoneContent />
              </PhoneShell>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingTop: 20 }}>
                {[
                  { icon: 'cash-outline',    val: '4.8M', unit: 'RWF', label: "Today's revenue"  },
                  { icon: 'radio-outline',   val: '84',   unit: '',    label: 'Live right now'   },
                  { icon: 'flash-outline',   val: '2.1s', unit: '',    label: 'Avg order time'   },
                ].map(({ icon, val, unit, label }) => (
                  <div key={label} style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 14, padding: '14px 16px', minWidth: 136, backdropFilter: 'blur(10px)' }}>
                    <Ion name={icon} size={18} color={C.orange} style={{ marginBottom: 6 }} />
                    <div style={{ fontFamily: F.sans, fontSize: 20, fontWeight: 800, color: C.white, letterSpacing: '-0.03em', lineHeight: 1 }}>{val} <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', fontWeight: 400 }}>{unit}</span></div>
                    <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', marginTop: 4, textTransform: 'uppercase', letterSpacing: '0.07em', fontWeight: 500, fontFamily: F.sans }}>{label}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ══ STATS BAR ═════════════════════════════════════════════ */}
      <section style={{ background: C.dark, borderTop: `1px solid ${C.darkLine}` }}>
        <div ref={statRef} style={{
          maxWidth: 1200, margin: '0 auto',
          padding: mob ? '40px 20px' : '56px 48px',
          display: 'grid',
          gridTemplateColumns: mob ? '1fr 1fr' : 'repeat(4, 1fr)',
          gap: mob ? '32px 24px' : 32,
          alignItems: 'center',
        }}>
          {[
            { n: 12400, suffix: '+', label: 'Active Users'    },
            { n: 840,   suffix: '+', label: 'Live Sellers'    },
            { n: 48000, suffix: '+', label: 'Products Listed' },
            { n: 98,    suffix: '%', label: 'Satisfaction'    },
          ].map(({ n, suffix, label }) => (
            <StatCount key={label} n={n} suffix={suffix} label={label} run={statVis} />
          ))}
        </div>
      </section>

      {/* ══ HOW IT WORKS ══════════════════════════════════════════ */}
      <section id="how" style={{ background: C.white, padding: mob ? '64px 20px' : '100px 48px' }}>
        <div ref={howRef} style={{
          maxWidth: 1200, margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: mob ? '1fr' : '1fr 1fr',
          gap: mob ? 48 : 96,
          alignItems: 'center',
        }}>

          <div>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: 'rgba(232,68,10,0.07)', border: '1px solid rgba(232,68,10,0.14)', borderRadius: 99, padding: '5px 13px', marginBottom: 20 }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: C.orange, letterSpacing: '0.07em', textTransform: 'uppercase', fontFamily: F.sans }}>How it works</span>
            </div>
            <h2 style={{ fontFamily: F.sans, fontSize: 'clamp(26px,4vw,48px)', fontWeight: 900, letterSpacing: '-0.04em', lineHeight: 1.06, color: C.text, marginBottom: 40 }}>
              From first tap<br />to your doorstep —<br /><span style={{ color: C.orange }}>ridiculously fast.</span>
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              {STEPS.map(({ n, icon, title, desc }, i) => (
                <div key={n} style={{ display: 'flex', gap: 20, alignItems: 'flex-start', paddingBottom: i < STEPS.length - 1 ? 28 : 0, opacity: howVis ? 1 : 0, transform: howVis ? 'none' : 'translateX(-14px)', transition: `all .55s ${i * 0.1}s ease`, position: 'relative' }}>
                  {i < STEPS.length - 1 && <div style={{ position: 'absolute', left: 22, top: 48, width: 1, bottom: 0, background: `linear-gradient(to bottom, ${C.border}, transparent)` }} />}
                  <div style={{ width: 44, height: 44, borderRadius: 12, background: i === 0 ? C.orange : C.snow, border: `1px solid ${i === 0 ? 'transparent' : C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Ion name={icon} size={20} color={i === 0 ? '#fff' : C.textSoft} />
                  </div>
                  <div style={{ paddingTop: 6 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: C.orange, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 4, fontFamily: F.sans }}>Step {n}</div>
                    <div style={{ fontFamily: F.sans, fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 5, letterSpacing: '-0.01em' }}>{title}</div>
                    <div style={{ fontSize: 13.5, color: C.textMed, lineHeight: 1.65 }}>{desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Phone 2 — desktop only */}
          {!mob && (
            <div style={{ display: 'flex', justifyContent: 'center', opacity: howVis ? 1 : 0, transform: howVis ? 'none' : 'translateY(22px)', transition: 'all .7s .2s ease' }}>
              <PhoneShell style={{ animation: 'kfloat 5s ease-in-out infinite' }}>
                <div style={{ width: '100%', height: '100%', background: '#060D1F', display: 'flex', flexDirection: 'column', position: 'relative' }}>
                  <div style={{ padding: '22px 12px 0', display: 'flex', justifyContent: 'space-between' }}>
                    <div style={{ background: 'rgba(255,255,255,0.08)', borderRadius: 7, padding: '3px 9px', display: 'flex', alignItems: 'center', gap: 4 }}>
                      <Ion name="play-circle-outline" size={10} color="rgba(255,255,255,0.7)" />
                      <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.7)', fontWeight: 700, fontFamily: F.sans }}>REEL</span>
                    </div>
                    <div style={{ background: 'rgba(255,255,255,0.08)', borderRadius: 7, padding: '2px 8px', display: 'flex', alignItems: 'center', gap: 4 }}>
                      <Ion name="eye-outline" size={9} color="rgba(255,255,255,0.5)" />
                      <span style={{ fontSize: 9, fontWeight: 600, color: 'rgba(255,255,255,0.6)', fontFamily: F.sans }}>940</span>
                    </div>
                  </div>
                  <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ width: 80, height: 80, borderRadius: '50%', background: 'rgba(13,107,62,0.15)', border: '1px solid rgba(13,107,62,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Ion name="football-outline" size={44} color="#2dbb7a" />
                    </div>
                  </div>
                  <div style={{ padding: '0 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 24, height: 24, borderRadius: '50%', background: '#0D6B3E', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 800, color: '#fff', fontFamily: F.sans }}>S</div>
                    <div style={{ fontSize: 10, fontWeight: 600, color: 'rgba(255,255,255,0.8)', fontFamily: F.sans }}>Sports Hub RW</div>
                  </div>
                  <div style={{ padding: '8px 12px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {[
                      { name: 'Pro Training Kit', price: '42,000', icon: 'football-outline', c: '#0D6B3E' },
                      { name: 'Running Cleats',   price: '38,000', icon: 'walk-outline',     c: '#0D6B3E' },
                    ].map(p => (
                      <div key={p.name} style={{ background: 'rgba(255,255,255,0.05)', borderRadius: 11, padding: '8px 10px', display: 'flex', alignItems: 'center', gap: 9, border: '1px solid rgba(255,255,255,0.07)' }}>
                        <div style={{ width: 32, height: 32, borderRadius: 9, background: 'rgba(13,107,62,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Ion name={p.icon} size={16} color="#2dbb7a" />
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 9, fontWeight: 600, color: 'rgba(255,255,255,0.7)', fontFamily: F.sans }}>{p.name}</div>
                          <div style={{ fontSize: 11, fontWeight: 800, color: C.orange, fontFamily: F.sans }}>RWF {p.price}</div>
                        </div>
                        <button style={{ background: '#fff', color: C.dark, border: 'none', borderRadius: 7, padding: '4px 9px', fontSize: 8.5, fontWeight: 700, cursor: 'pointer', fontFamily: F.sans }}>Buy</button>
                      </div>
                    ))}
                  </div>
                  <div style={{ padding: '6px 12px 14px', display: 'flex', gap: 6, alignItems: 'center' }}>
                    <div style={{ flex: 1, background: 'rgba(255,255,255,0.05)', borderRadius: 16, padding: '6px 10px', fontSize: 9, color: 'rgba(255,255,255,0.25)', fontFamily: F.sans, border: '1px solid rgba(255,255,255,0.06)' }}>Comment…</div>
                    <div style={{ width: 26, height: 26, borderRadius: '50%', background: 'rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(255,255,255,0.08)' }}>
                      <Ion name="heart-outline" size={13} color="rgba(255,255,255,0.4)" />
                    </div>
                  </div>
                </div>
              </PhoneShell>
            </div>
          )}
        </div>
      </section>

      {/* ══ FOR BUYERS & SELLERS ══════════════════════════════════ */}
      <section id="buyers" style={{ background: C.snow, padding: mob ? '64px 20px' : '100px 48px' }}>
        <div ref={forRef} style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: mob ? 36 : 56 }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: 'rgba(232,68,10,0.07)', border: '1px solid rgba(232,68,10,0.14)', borderRadius: 99, padding: '5px 13px', marginBottom: 16 }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: C.orange, letterSpacing: '0.07em', textTransform: 'uppercase', fontFamily: F.sans }}>Who it's for</span>
            </div>
            <h2 style={{ fontFamily: F.sans, fontSize: 'clamp(24px,4vw,48px)', fontWeight: 900, letterSpacing: '-0.04em', color: C.text }}>
              Built for every side<br />of the marketplace
            </h2>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: mob ? '1fr' : '1fr 1fr', gap: mob ? 16 : 18 }}>
            {/* Buyers */}
            <div id="buyers-card" style={{ background: C.white, borderRadius: 22, padding: mob ? '28px 22px' : '40px 36px', border: `1px solid ${C.border}`, opacity: forVis ? 1 : 0, transform: forVis ? 'none' : 'translateX(-20px)', transition: 'all .6s ease' }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: '#EFF6FF', borderRadius: 8, padding: '5px 12px', marginBottom: 20 }}>
                <Ion name="bag-outline" size={13} color="#2563EB" />
                <span style={{ fontSize: 11, fontWeight: 700, color: '#2563EB', letterSpacing: '0.07em', textTransform: 'uppercase', fontFamily: F.sans }}>For Buyers</span>
              </div>
              <h3 style={{ fontFamily: F.sans, fontSize: mob ? 22 : 26, fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.18, color: C.text, marginBottom: 10 }}>Discover products<br />while they're live</h3>
              <p style={{ fontSize: 14, color: C.textMed, lineHeight: 1.72, marginBottom: 24 }}>No more static photos. Watch sellers demo products live, ask questions in real time, and buy with one tap — all from your phone.</p>
              <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 28 }}>
                {['Watch 800+ live streams every day','Ask sellers questions as they go live','Pay instantly with MTN MoMo or Airtel','Same-day delivery anywhere in Kigali','Track your driver live on the map','Buyer protection on every single order'].map(f => (
                  <CheckItem key={f} iconColor="#2563EB">{f}</CheckItem>
                ))}
              </ul>
              <button style={{ height: 42, padding: '0 20px', borderRadius: 10, border: '1.5px solid #BFDBFE', background: '#EFF6FF', color: '#2563EB', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: F.sans, display: 'flex', alignItems: 'center', gap: 7 }}>
                Start Shopping <Ion name="arrow-forward-outline" size={15} color="#2563EB" />
              </button>
            </div>

            {/* Sellers */}
            <div id="sellers" style={{ background: C.dark, borderRadius: 22, padding: mob ? '28px 22px' : '40px 36px', border: `1px solid ${C.darkLine}`, opacity: forVis ? 1 : 0, transform: forVis ? 'none' : 'translateX(20px)', transition: 'all .6s .1s ease' }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: 'rgba(232,68,10,0.12)', border: '1px solid rgba(232,68,10,0.2)', borderRadius: 8, padding: '5px 12px', marginBottom: 20 }}>
                <Ion name="storefront-outline" size={13} color={C.orange} />
                <span style={{ fontSize: 11, fontWeight: 700, color: C.orange, letterSpacing: '0.07em', textTransform: 'uppercase', fontFamily: F.sans }}>For Sellers</span>
              </div>
              <h3 style={{ fontFamily: F.sans, fontSize: mob ? 22 : 26, fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.18, color: C.white, marginBottom: 10 }}>Your live audience<br />is your storefront</h3>
              <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.5)', lineHeight: 1.72, marginBottom: 24 }}>Turn your passion into a business. Go live, pin products, earn — all from your phone. No studio, no equipment, no experience needed.</p>
              <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 28 }}>
                {['Go live in seconds — phone camera only','Pin products during stream for instant sales','Post shoppable reels between live sessions','Revenue, viewers & analytics in real time','Payouts directly to MTN MoMo or Airtel','Full order & inventory management built in'].map(f => (
                  <li key={f} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 13.5, color: 'rgba(255,255,255,0.55)', lineHeight: 1.55 }}>
                    <div style={{ width: 20, height: 20, borderRadius: '50%', background: 'rgba(232,68,10,0.15)', border: '1px solid rgba(232,68,10,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
                      <Ion name="checkmark-outline" size={12} color={C.orange} />
                    </div>
                    {f}
                  </li>
                ))}
              </ul>
              <button style={{ height: 42, padding: '0 20px', borderRadius: 10, border: 'none', background: C.orange, color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: F.sans, display: 'flex', alignItems: 'center', gap: 7, boxShadow: '0 6px 20px rgba(232,68,10,0.28)' }}>
                Open Your Shop <Ion name="arrow-forward-outline" size={15} color="#fff" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ══ FEATURES ══════════════════════════════════════════════ */}
      <section id="features" style={{ background: C.white, padding: mob ? '64px 20px' : '100px 48px' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: mob ? 36 : 56 }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: 'rgba(232,68,10,0.07)', border: '1px solid rgba(232,68,10,0.14)', borderRadius: 99, padding: '5px 13px', marginBottom: 16 }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: C.orange, letterSpacing: '0.07em', textTransform: 'uppercase', fontFamily: F.sans }}>Platform features</span>
            </div>
            <h2 style={{ fontFamily: F.sans, fontSize: 'clamp(24px,4vw,48px)', fontWeight: 900, letterSpacing: '-0.04em', color: C.text, marginBottom: 12 }}>
              Everything you need,<br />nothing you don't
            </h2>
            <p style={{ fontSize: 15, color: C.textMed, maxWidth: 420, margin: '0 auto', lineHeight: 1.7, fontFamily: F.sans }}>
              Built from scratch for the Rwandan market — fast, affordable, and mobile-first.
            </p>
          </div>
          <div ref={featRef} style={{ display: 'grid', gridTemplateColumns: mob ? '1fr 1fr' : 'repeat(3, 1fr)', gap: mob ? 12 : 14 }}>
            {FEATURES.map(({ icon, title, desc }, i) => (
              <FeatureCard key={title} icon={icon} title={title} desc={desc} delay={i * 0.07} vis={featVis} />
            ))}
          </div>
        </div>
      </section>

      {/* ══ METRICS STRIP ═════════════════════════════════════════ */}
      <section style={{ background: C.snow, padding: mob ? '0 20px' : '0 48px', borderTop: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}` }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', display: 'grid', gridTemplateColumns: mob ? '1fr' : 'repeat(3, 1fr)', padding: mob ? '36px 0' : '52px 0', gap: mob ? 28 : 0 }}>
          {[
            { icon: 'time-outline',           val: '2.1s',  label: 'Average order time'  },
            { icon: 'phone-portrait-outline', val: '1 tap', label: 'To complete checkout' },
            { icon: 'star-outline',           val: '98%',   label: 'Buyer satisfaction'  },
          ].map(({ icon, val, label }, i) => (
            <div key={label} style={{ textAlign: 'center', padding: mob ? '0' : '0 24px', borderRight: !mob && i < 2 ? `1px solid ${C.border}` : 'none' }}>
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 10 }}>
                <Ion name={icon} size={22} color={C.orange} />
              </div>
              <div style={{ fontFamily: F.sans, fontSize: mob ? 28 : 34, fontWeight: 900, letterSpacing: '-0.05em', color: C.text, marginBottom: 4 }}>{val}</div>
              <div style={{ fontSize: 12, color: C.textSoft, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: F.sans }}>{label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ══ CTA ═══════════════════════════════════════════════════ */}
      <section style={{ padding: mob ? '40px 20px 60px' : '80px 48px 100px', background: C.white }}>
        <div ref={ctaRef} style={{ maxWidth: 1200, margin: '0 auto', background: C.dark, borderRadius: mob ? 18 : 24, padding: mob ? '48px 24px' : '80px 64px', textAlign: 'center', position: 'relative', overflow: 'hidden', opacity: ctaVis ? 1 : 0, transform: ctaVis ? 'none' : 'translateY(22px)', transition: 'all .65s ease' }}>
          <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 600, height: 300, background: 'radial-gradient(ellipse, rgba(232,68,10,0.12) 0%, transparent 70%)', pointerEvents: 'none' }} />
          <div style={{ position: 'absolute', inset: 0, backgroundImage: `repeating-linear-gradient(0deg, transparent, transparent 71px, rgba(255,255,255,0.02) 71px, rgba(255,255,255,0.02) 72px), repeating-linear-gradient(90deg, transparent, transparent 71px, rgba(255,255,255,0.02) 71px, rgba(255,255,255,0.02) 72px)`, pointerEvents: 'none' }} />

          <div style={{ position: 'relative' }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: 'rgba(232,68,10,0.12)', border: '1px solid rgba(232,68,10,0.25)', borderRadius: 99, padding: '5px 13px', marginBottom: 20 }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: C.orange, animation: 'kpulse 1.4s infinite' }} />
              <span style={{ fontSize: 11, fontWeight: 600, color: C.orange, letterSpacing: '0.06em', textTransform: 'uppercase', fontFamily: F.sans }}>Rwanda's live commerce platform</span>
            </div>
            <h2 style={{ fontFamily: F.sans, fontSize: mob ? 'clamp(28px,8vw,42px)' : 'clamp(30px,5vw,58px)', fontWeight: 900, letterSpacing: '-0.05em', lineHeight: 1.04, color: C.white, marginBottom: 14 }}>
              Your stream.<br />Your store.<br />Your market.
            </h2>
            <p style={{ fontSize: mob ? 14 : 16, color: 'rgba(255,255,255,0.45)', maxWidth: 420, margin: '0 auto 32px', lineHeight: 1.7, fontFamily: F.sans }}>
              Whether you're here to shop or sell — your spot in Rwanda's live commerce revolution is waiting.
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
              <button style={{ height: 48, padding: '0 22px', borderRadius: 11, border: 'none', background: C.white, color: C.dark, fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: F.sans, display: 'flex', alignItems: 'center', gap: 8 }}>
                <Ion name="logo-android" size={17} color={C.dark} />
                Download for Android
              </button>
              <button style={{ height: 48, padding: '0 22px', borderRadius: 11, border: '1px solid rgba(255,255,255,0.16)', background: 'rgba(255,255,255,0.06)', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: F.sans, display: 'flex', alignItems: 'center', gap: 8 }}>
                <Ion name="logo-apple" size={17} color="#fff" />
                Download for iOS
              </button>
            </div>
            <p style={{ marginTop: 18, fontSize: 12, color: 'rgba(255,255,255,0.25)', fontFamily: F.sans }}>Free to download · No subscription fees · Open your shop in minutes</p>
          </div>
        </div>
      </section>

      {/* ══ FOOTER ════════════════════════════════════════════════ */}
      <footer style={{
        borderTop: `1px solid ${C.border}`,
        padding: mob ? '24px 20px' : '32px 48px',
        background: C.white,
        display: 'flex',
        flexDirection: mob ? 'column' : 'row',
        alignItems: mob ? 'flex-start' : 'center',
        justifyContent: 'space-between',
        gap: mob ? 20 : 16,
      }}>
        <KudosLogo size={26} textSize={14} textColor={C.text} />
        <div style={{ display: 'flex', gap: mob ? 16 : 24, flexWrap: 'wrap' }}>
          {[['Privacy Policy','/privacy'],['Terms of Service','/terms']].map(([l, href]) => (
            <a key={l} href={href} style={{ fontSize: 12.5, color: C.textSoft, fontFamily: F.sans, transition: 'color .2s' }}
              onMouseEnter={e => e.currentTarget.style.color = C.text}
              onMouseLeave={e => e.currentTarget.style.color = C.textSoft}>{l}</a>
          ))}
          {[['Help Center', () => setShowHelp(true)], ['Contact', () => setShowContact(true)]].map(([l, handler]) => (
            <button key={l} onClick={handler} style={{ fontSize: 12.5, color: C.textSoft, background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontFamily: F.sans, transition: 'color .2s' }}
              onMouseEnter={e => e.currentTarget.style.color = C.text}
              onMouseLeave={e => e.currentTarget.style.color = C.textSoft}>{l}</button>
          ))}
        </div>
        <div style={{ fontSize: 12.5, color: C.textFaint, fontFamily: F.sans, display: 'flex', alignItems: 'center', gap: 5 }}>
          <Ion name="flag-outline" size={12} color={C.textFaint} />
          Made in Kigali · © 2025 Kudos Live
        </div>
      </footer>

      {showHelp    && <HelpCenterModal onClose={() => setShowHelp(false)}    onContact={() => { setShowHelp(false); setShowContact(true) }} mob={mob} />}
      {showContact && <ContactModal    onClose={() => setShowContact(false)}  mob={mob} />}
    </div>
  )
}
